/**
 * The ledger cache.
 *
 * THIS IS THE MOST IMPORTANT PERFORMANCE DECISION IN THE APP.
 *
 * Voucher entry needs type-ahead on every keystroke. Calling the server
 * each time costs 200-400ms per character over a mobile connection, which
 * is the difference between "fast" and "unusable" -- and it is what the
 * original project did.
 *
 * Instead we fetch the whole chart ONCE after login and filter locally.
 * A 3,700-account chart is roughly 300KB of JSON; filtering it takes well
 * under a millisecond, so the dropdown updates within a single frame.
 *
 * Search is SUBSTRING, not prefix: typing "rajhi" must find
 * "Al Rajhi Trading Est.". Prefix matching would miss it, and users would
 * have to remember how every account name begins.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef,
  useState, type ReactNode,
} from 'react'
import { fetchLedgers } from '../api/endpoints'
import type { Ledger } from '../api/types'

interface SearchableLedger extends Ledger {
  /** Pre-lowercased haystack, built once so search does no work per keystroke. */
  _haystack: string
}

interface LedgerCacheValue {
  ledgers: Ledger[]
  loading: boolean
  error: string | null
  /** Filter locally. Returns [] for an empty query. */
  search: (query: string, limit?: number) => Ledger[]
  byCode: (code: string) => Ledger | undefined
  /** Re-fetch after creating a ledger. */
  refresh: () => Promise<void>
  /** Insert a newly created ledger without a round trip (Alt+C flow). */
  add: (led: Ledger) => void
}

const Ctx = createContext<LedgerCacheValue | null>(null)

export function LedgerCacheProvider({ children }: { children: ReactNode }) {
  const [ledgers, setLedgers] = useState<Ledger[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Kept in a ref so search() never changes identity and never re-renders.
  const indexRef = useRef<SearchableLedger[]>([])
  const mapRef = useRef<Map<string, Ledger>>(new Map())

  const build = useCallback((list: Ledger[]) => {
    indexRef.current = list.map((l) => ({
      ...l,
      _haystack: `${l.code} ${l.name_en} ${l.name_ar ?? ''}`.toLowerCase(),
    }))
    mapRef.current = new Map(list.map((l) => [l.code, l]))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await fetchLedgers()
      build(list)
      setLedgers(list)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load accounts')
    } finally {
      setLoading(false)
    }
  }, [build])

  useEffect(() => { void load() }, [load])

  const search = useCallback((query: string, limit = 20): Ledger[] => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    const out: Ledger[] = []
    // Exact code match first, then prefix, then substring: the ordering
    // users expect when they type a code they already know.
    const exact: Ledger[] = []
    const prefix: Ledger[] = []
    const contains: Ledger[] = []
    for (const l of indexRef.current) {
      if (l.code.toLowerCase() === q) exact.push(l)
      else if (l._haystack.startsWith(q)) prefix.push(l)
      else if (l._haystack.includes(q)) contains.push(l)
      if (exact.length + prefix.length + contains.length >= limit * 3) break
    }
    out.push(...exact, ...prefix, ...contains)
    return out.slice(0, limit)
  }, [])

  const byCode = useCallback(
    (code: string) => mapRef.current.get(code), [])

  const add = useCallback((led: Ledger) => {
    setLedgers((prev) => {
      const next = [...prev.filter((l) => l.code !== led.code), led]
        .sort((a, b) => a.code.localeCompare(b.code))
      build(next)
      return next
    })
  }, [build])

  const value = useMemo<LedgerCacheValue>(
    () => ({ ledgers, loading, error, search, byCode, refresh: load, add }),
    [ledgers, loading, error, search, byCode, load, add],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useLedgers(): LedgerCacheValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useLedgers must be used inside <LedgerCacheProvider>')
  return v
}
